import type { JSX } from 'react';
import { useLocation } from 'react-router';

import { buildPageMeta, type PageMetaOverrides } from '@/seo/meta';

/**
 * React 19 hoists `title`, `meta` and `link` elements into the document head,
 * so no metadata library is needed. During prerendering these tags are written
 * straight into the static HTML from the same `buildPageMeta` source, and this
 * component is skipped, which keeps the two from ever disagreeing.
 */
export function Seo(overrides: PageMetaOverrides = {}): JSX.Element | null {
  const { pathname } = useLocation();

  if (import.meta.env.SSR) {
    return null;
  }

  const meta = buildPageMeta(pathname, overrides);

  return (
    <>
      <title>{meta.title}</title>
      <link rel="canonical" href={meta.canonical} />
      {meta.tags.map((tag) =>
        tag.kind === 'name' ? (
          <meta key={tag.key} name={tag.key} content={tag.content} />
        ) : (
          <meta key={tag.key} property={tag.key} content={tag.content} />
        ),
      )}
      {meta.jsonLd.map((entry) => (
        <script key={entry['@type']} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </>
  );
}
